import React from 'react';

/**
 * WizardProgress - Progress bar component for the wizard
 *
 * Displays:
 * - Step X av Y text
 * - Percentage complete
 * - Visual progress bar with peach accent
 * - Clickable step dots for navigation
 *
 * Dot states:
 * - Current step: peach filled with ring highlight
 * - Steps before current: small solid dark dots
 * - Steps after current: hollow/outline grey dots
 */
export function WizardProgress({
  currentStep,
  totalSteps,
  showDots = true,
  onStepClick,
  allStepsNavigable = false, // When true, all dots are clickable (for existing drafts)
}) {
  const progress = totalSteps > 1
    ? Math.round(((currentStep - 1) / (totalSteps - 1)) * 100)
    : 0;

  return (
    <div className="mb-8">
      {/* Text indicators */}
      <div className="flex justify-between items-center mb-3">
        <span className="text-sm font-medium text-text-secondary">
          Steg {currentStep} av {totalSteps}
        </span>
        <span className="text-sm text-text-muted">
          {progress}% fullført
        </span>
      </div>

      {/* Progress bar */}
      <div className="relative">
        <div className="h-2 bg-neutral-200 rounded-full overflow-hidden">
          <div
            className="h-2 bg-primary rounded-full transition-all duration-500 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Step dots */}
        {showDots && totalSteps <= 10 && (
          <div className="absolute -top-1 left-0 w-full flex justify-between">
            {Array.from({ length: totalSteps }, (_, i) => {
              const stepNumber = i + 1;
              const isCurrent = stepNumber === currentStep;
              const isBeforeCurrent = stepNumber < currentStep;

              // Clickable if: not current AND (all steps navigable OR step is before current)
              const isClickable = onStepClick && !isCurrent && (allStepsNavigable || isBeforeCurrent);

              // Determine dot style based on state (original design)
              let dotStyle = '';
              if (isCurrent) {
                // Current step: peach filled with highlight ring
                dotStyle = 'bg-primary border-primary ring-2 ring-primary-200 ring-offset-1';
              } else if (isBeforeCurrent) {
                // Steps before current: small solid dark dots
                dotStyle = 'bg-neutral-600 border-neutral-600';
              } else {
                // Steps after current: hollow/outline grey dots
                dotStyle = 'bg-white border-neutral-300';
              }

              return (
                <button
                  key={i}
                  type="button"
                  disabled={!isClickable}
                  onClick={() => isClickable && onStepClick(i)}
                  className={`
                    w-4 h-4 rounded-full border-2 transition-all duration-300
                    ${dotStyle}
                    ${isClickable
                      ? 'cursor-pointer hover:scale-110'
                      : 'cursor-default'
                    }
                  `}
                  aria-label={`Steg ${stepNumber}`}
                  title={
                    isCurrent
                      ? `Steg ${stepNumber} (nåværende)`
                      : isClickable
                        ? `Gå til steg ${stepNumber}`
                        : `Steg ${stepNumber}`
                  }
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default WizardProgress;
